import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Apollo, gql } from 'apollo-angular';
import { FeatureCollection, Geometry } from 'geojson';
import { Observable, Subject } from 'rxjs';
import { filter, map, tap } from 'rxjs/operators';
import { FlyFreelyConstants } from '../constants';
import { AltitudeValue } from '../model/altitude';
import {
    CurrentAttachmentVersionDto,
    ImageReference,
    LinkDto,
    LocationFeatureDto,
    MissionNoticeAcknowledgementDto,
    ResultDto,
    SimpleMissionDto,
    SimpleOrganisationDto,
    SimplePersonDto
} from '../model/api';
import { DateRange, GqlQueryResult, Pagination } from './interfaces';
import { ChangeDetails } from './service.helpers';

interface Notice {
    id: number;
    subject: string;
    description: string;
    startTime: string;
    endTime?: string;
    pointOfContact?: string;
    pointOfContactPhoneNumber?: string;
    importance: NoticeImportance;
    status: NoticeStatus;
    currentUserAcknowledgementTime: string;
    operatingRequirement: NoticeOperatingRequirement;
    attachmentList: CurrentAttachmentVersionDto[];
    linkList: LinkDto[];
    audience: NoticeAudience;
    upperLimit: AltitudeValue;
    lowerLimit: AltitudeValue;
    noticeType: NoticeType;
    organisation: {
        id: number;
        name: string;
    };
}

export interface EditNotice extends Notice {
    location: LocationFeatureDto[];
}

export interface DisplayNotice extends Notice {
    displayLocation: {
        type: 'FeatureCollectionWithSprites';
        spriteList: ImageReference[];
        featureCollection: FeatureCollection;
    };
}

export interface NoticeType {
    id: number;
    name: string;
    archived: boolean;
    description?: string;
    icon?: string;
    iconFamily?: string;
    colour?: string;
    audience: NoticeAudience;
}

export interface CreateNoticeTypeCommand {
    organisationId: number;
    audience: NoticeAudience;
    name: string;
    description?: string;
    icon?: string;
    iconFamily?: string;
    colour?: string;
}

export interface UpdateNoticeTypeCommand {
    id: number;
    name: string;
    description?: string;
    icon?: string;
    iconFamily?: string;
    colour?: string;
}

export interface Lotam extends DisplayNotice {
    isAcknowledged: boolean;
}

/** A notice with both raw editable features and a server-rendered display location. */
export interface FullNotice extends EditNotice {
    displayLocation: DisplayNotice['displayLocation'];
}

/** A FullNotice decorated with the client-side isAcknowledged flag. */
export interface LotamFull extends FullNotice {
    isAcknowledged: boolean;
}

export interface LocationFeatureInput {
    name: string;
    type: LocationFeatureDto.Type;
    geometry: Geometry;
    properties: JSON;
}

export interface UpdateNoticeCommand {
    id: number;
    subject: string;
    description: string;
    startTime: string;
    endTime?: string;
    pointOfContact?: string;
    pointOfContactPhoneNumber?: string;
    importance: NoticeImportance;
    operatingRequirement: NoticeOperatingRequirement;
    location: LocationFeatureInput[];
    upperLimit?: AltitudeValue;
    lowerLimit?: AltitudeValue;
    noticeTypeId?: number;
}

export type NoticeOperatingRequirement = 'NONE' | 'ACKNOWLEDGE' | 'BLOCK';
export const NoticeOperatingRequirement = {
    NONE: 'NONE' as NoticeOperatingRequirement,
    ACKNOWLEDGE: 'ACKNOWLEDGE' as NoticeOperatingRequirement,
    BLOCK: 'BLOCK' as NoticeOperatingRequirement
};

export type NoticeImportance = 'LOW' | 'MEDIUM' | 'HIGH';
export const NoticeImportance = {
    LOW: 'LOW' as NoticeImportance,
    MEDIUM: 'MEDIUM' as NoticeImportance,
    HIGH: 'HIGH' as NoticeImportance
};

export interface NoticeSortField {
    field: string;
    order?: 'ASC' | 'DESC';
}

export type NoticeStatus = 'NEW' | 'PENDING' | 'ACTIVE' | 'CANCELLED';

export type NoticeAudience = 'GENERAL' | 'FLIGHT_OPERATIONS';

export interface NoticeAcknowledgement {
    person: SimplePersonDto;
    acknowledgementTime: string;
}

export interface NoticeMissionAcknowledgementDto {
    person: SimplePersonDto;
    acknowledgementTime: string;
}

export interface NoticeMissionDto {
    mission: SimpleMissionDto;
    status: string;
    acknowledgements: NoticeMissionAcknowledgementDto[];
}

export interface NoticeCriteria {
    organisationId: number;
    time?: DateRange;
    subject?: string;
    description?: string;
    importance?: NoticeImportance;
    acknowledged?: boolean;
    text?: string;
    audience?: NoticeAudience;
    noticeTypeId?: number;
    includedSiteIdList?: number[];
}

@Injectable({
    providedIn: 'root'
})
export class NoticesService {
    private changeSource = new Subject<ChangeDetails<Notice>>();
    change$ = this.changeSource.asObservable();

    private ngUnsubscribe$ = new Subject<void>();
    private baseUrl: string;
    constructor(
        private apollo: Apollo,
        private http: HttpClient,
        constants: FlyFreelyConstants
    ) {
        this.baseUrl = constants.API_URL;
    }

    ngOnDestroy() {
        this.ngUnsubscribe$.next();
        this.ngUnsubscribe$.complete();
    }

    findNotices(
        criteria: NoticeCriteria,
        pagination: Pagination
    ): Observable<GqlQueryResult<DisplayNotice>> {
        return this.apollo
            .query<{
                findNotices: GqlQueryResult<DisplayNotice>;
            }>({
                query: gql`
                    query findNotices(
                        $criteria: NoticeCriteria!
                        $sortFields: [NoticeSortField]
                        $page: Int
                        $pageSize: Int
                    ) {
                        findNotices(
                            criteria: $criteria
                            sortFields: $sortFields
                            page: $page
                            pageSize: $pageSize
                        ) {
                            count
                            results {
                                id
                                subject
                                description
                                startTime
                                endTime
                                pointOfContact
                                pointOfContactPhoneNumber
                                importance
                                status
                                audience
                                requiresAcknowledgement
                                operatingRequirement
                                currentUserAcknowledgementTime
                                displayLocation {
                                    type: __typename
                                    spriteList {
                                        id
                                        url
                                    }
                                    featureCollection {
                                        type
                                        features {
                                            type
                                            properties
                                            geometry
                                        }
                                    }
                                }
                                upperLimit {
                                    height
                                    units
                                    datum
                                }
                                lowerLimit {
                                    height
                                    units
                                    datum
                                }
                                noticeType {
                                    id
                                    name
                                    archived
                                    description
                                    icon
                                    iconFamily
                                    colour
                                    audience
                                }
                                organisation {
                                    id
                                    name
                                }
                            }
                        }
                    }
                `,
                variables: {
                    criteria,
                    ...pagination
                },
                fetchPolicy: 'network-only'
            })
            .pipe(
                filter(r => !r.loading),
                map(r => ({ ...r.data.findNotices, pagination }))
            );
    }

    countNoticesRequiringAcknowledgement(
        organisationId: number
    ): Observable<number> {
        return this.apollo
            .query<{
                countRequiredNoticeAcknowledgements: number;
            }>({
                query: gql`
                    query countRequiredNoticeAcknowledgements(
                        $organisationId: Long!
                    ) {
                        countRequiredNoticeAcknowledgements(
                            organisationId: $organisationId
                        )
                    }
                `,
                variables: {
                    organisationId
                },
                fetchPolicy: 'network-only'
            })
            .pipe(
                filter(r => !r.loading),
                map(r => r.data.countRequiredNoticeAcknowledgements)
            );
    }

    previewNoticeDisplayLocation(
        command: UpdateNoticeCommand
    ): Observable<DisplayNotice['displayLocation']> {
        return this.apollo
            .query<{
                previewNoticeDisplayLocation: DisplayNotice['displayLocation'];
            }>({
                query: gql`
                    query previewNoticeDisplayLocation(
                        $command: UpdateNoticeCommand!
                    ) {
                        previewNoticeDisplayLocation(command: $command) {
                            type: __typename
                            spriteList {
                                id
                                url
                            }
                            featureCollection {
                                type
                                features {
                                    type
                                    properties
                                    geometry
                                }
                            }
                        }
                    }
                `,
                variables: { command },
                fetchPolicy: 'network-only'
            })
            .pipe(
                filter(r => !r.loading),
                map(r => r.data.previewNoticeDisplayLocation)
            );
    }

    updateNotice(command: UpdateNoticeCommand): Observable<FullNotice> {
        const updateCommand = Object.assign(command);
        return this.apollo
            .mutate<{ updateNotice: FullNotice }>({
                mutation: gql`
                    mutation updateNotice($updateCommand: UpdateNoticeCommand) {
                        updateNotice(command: $updateCommand) {
                            id
                            subject
                            description
                            startTime
                            endTime
                            pointOfContact
                            pointOfContactPhoneNumber
                            importance
                            status
                            audience
                            requiresAcknowledgement
                            operatingRequirement
                            location {
                                name
                                type
                                geometry
                                properties
                            }
                            displayLocation {
                                type: __typename
                                spriteList {
                                    id
                                    url
                                }
                                featureCollection {
                                    type
                                    features {
                                        type
                                        properties
                                        geometry
                                    }
                                }
                            }
                            upperLimit {
                                height
                                units
                                datum
                            }
                            lowerLimit {
                                height
                                units
                                datum
                            }
                            noticeType {
                                id
                                name
                                archived
                                description
                                icon
                                iconFamily
                                colour
                                audience
                            }
                            organisation {
                                id
                                name
                            }
                        }
                    }
                `,
                variables: { updateCommand },
                fetchPolicy: 'network-only'
            })
            .pipe(
                map(response => {
                    if (response?.data?.updateNotice) {
                        return response.data.updateNotice;
                    }
                    throw new Error('Create notice mutation failed');
                }),
                tap(result =>
                    this.changeSource.next({
                        type: ChangeDetails.Type.UPDATE,
                        changedId: result.id,
                        result
                    })
                )
            );
    }

    newNotice(
        organisationId: number,
        audience: NoticeAudience
    ): Observable<Notice> {
        return this.apollo
            .mutate<{ newNotice: Notice }>({
                mutation: gql`
                    mutation newNotice(
                        $organisationId: Long!
                        $audience: NoticeAudience!
                    ) {
                        newNotice(
                            organisationId: $organisationId
                            audience: $audience
                        ) {
                            id
                            subject
                            description
                            startTime
                            endTime
                            pointOfContact
                            pointOfContactPhoneNumber
                            importance
                            status
                            audience
                            upperLimit {
                                height
                                units
                                datum
                            }
                            lowerLimit {
                                height
                                units
                                datum
                            }
                            noticeType {
                                id
                                name
                                archived
                                description
                                icon
                                iconFamily
                                colour
                                audience
                            }
                            organisation {
                                id
                                name
                            }
                        }
                    }
                `,
                variables: { organisationId, audience }
            })
            .pipe(
                map(response => {
                    if (response?.data?.newNotice) {
                        return response.data.newNotice;
                    }
                    throw new Error('New notice mutation failed');
                }),
                tap(result => {
                    this.changeSource.next({
                        type: ChangeDetails.Type.CREATE,
                        changedId: result.id,
                        result
                    });
                })
            );
    }

    deleteNotice(noticeId: number): Observable<boolean> {
        return this.apollo
            .mutate<{ deleteNotice: { success: boolean } }>({
                mutation: gql`
                    mutation deleteNotice($noticeId: Long!) {
                        deleteNotice(noticeId: $noticeId) {
                            success
                        }
                    }
                `,
                variables: { noticeId }
            })
            .pipe(
                map(response => {
                    if (response?.data?.deleteNotice?.success) {
                        return response.data.deleteNotice.success;
                    }
                    throw new Error('Delete notice mutation failed');
                }),
                tap(() => {
                    this.changeSource.next({
                        type: ChangeDetails.Type.DELETE,
                        changedId: noticeId
                    });
                })
            );
    }

    findNoticeById(
        noticeId: number,
        managingOrganisationId: number
    ): Observable<FullNotice> {
        return this.apollo
            .query<{
                findNoticeById: FullNotice;
            }>({
                query: gql`
                    query findNoticeById(
                        $noticeId: Long!
                        $managingOrganisationId: Long!
                    ) {
                        findNoticeById(
                            noticeId: $noticeId
                            managingOrganisationId: $managingOrganisationId
                        ) {
                            id
                            subject
                            description
                            startTime
                            endTime
                            requiresAcknowledgement
                            operatingRequirement
                            currentUserAcknowledgementTime
                            audience
                            pointOfContact
                            pointOfContactPhoneNumber
                            importance
                            status
                            attachmentList {
                                id
                                attachmentVersionId
                                name
                                description
                                purpose
                                contentType
                                version
                                originalFileName
                                changes
                                uploadTime
                                requiresAcknowledgement
                                requirementId
                            }
                            location {
                                name
                                type
                                geometry
                                properties
                            }
                            displayLocation {
                                type: __typename
                                spriteList {
                                    id
                                    url
                                }
                                featureCollection {
                                    type
                                    features {
                                        type
                                        properties
                                        geometry
                                    }
                                }
                            }
                            linkList {
                                id
                                title
                                url
                            }
                            upperLimit {
                                height
                                units
                                datum
                            }
                            lowerLimit {
                                height
                                units
                                datum
                            }
                            noticeType {
                                id
                                name
                                archived
                                description
                                icon
                                iconFamily
                                colour
                                audience
                            }
                            organisation {
                                id
                                name
                            }
                        }
                    }
                `,
                variables: {
                    noticeId,
                    managingOrganisationId
                },
                fetchPolicy: 'network-only'
            })
            .pipe(
                map(response => {
                    if (response?.data?.findNoticeById) {
                        return response.data.findNoticeById;
                    }
                    throw new Error('findNoticeById query failed');
                })
            );
    }

    findNoticeAcknowledgements(
        noticeId: number,
        managingOrganisationId: number
    ): Observable<NoticeAcknowledgement[]> {
        return this.apollo
            .query<{
                findNoticeAcknowledgements: NoticeAcknowledgement[];
            }>({
                query: gql`
                    query findNoticeAcknowledgements(
                        $noticeId: Long!
                        $managingOrganisationId: Long!
                    ) {
                        findNoticeAcknowledgements(
                            noticeId: $noticeId
                            managingOrganisationId: $managingOrganisationId
                        ) {
                            person {
                                id
                                firstName
                                lastName
                            }
                            acknowledgementTime
                        }
                    }
                `,
                variables: {
                    noticeId,
                    managingOrganisationId
                },
                fetchPolicy: 'network-only'
            })
            .pipe(
                filter(r => !r.loading),
                map(r => r.data.findNoticeAcknowledgements)
            );
    }

    findNoticeMissions(
        noticeId: number,
        managingOrganisationId: number
    ): Observable<NoticeMissionDto[]> {
        return this.apollo
            .query<{
                findNoticeMissions: NoticeMissionDto[];
            }>({
                query: gql`
                    query findNoticeMissions(
                        $noticeId: Long!
                        $managingOrganisationId: Long!
                    ) {
                        findNoticeMissions(
                            noticeId: $noticeId
                            managingOrganisationId: $managingOrganisationId
                        ) {
                            mission {
                                id
                                name
                                missionDate
                                missionEstimatedTime
                                pointOfContact
                                pointOfContactInformation
                                organisationName
                            }
                            status
                            acknowledgements {
                                person {
                                    id
                                    firstName
                                    lastName
                                }
                                acknowledgementTime
                            }
                        }
                    }
                `,
                variables: {
                    noticeId,
                    managingOrganisationId
                },
                fetchPolicy: 'network-only'
            })
            .pipe(
                filter(r => !r.loading),
                map(r => r.data.findNoticeMissions)
            );
    }

    findNoticeTypes(criteria: {
        organisationId: number;
        managingOrganisationId: number;
        audience?: NoticeAudience;
        archived?: boolean;
    }) {
        return this.apollo
            .query<{
                findNoticeTypes: NoticeType[];
            }>({
                query: gql`
                    query findNoticeTypes(
                        $organisationId: Long!
                        $managingOrganisationId: Long!
                        $audience: NoticeAudience
                        $archived: Boolean
                    ) {
                        findNoticeTypes(
                            criteria: {
                                organisationId: $organisationId
                                managingOrganisationId: $managingOrganisationId
                                audience: $audience
                                archived: $archived
                            }
                        ) {
                            id
                            name
                            archived
                            description
                            icon
                            iconFamily
                            colour
                            audience
                        }
                    }
                `,
                variables: criteria,
                fetchPolicy: 'network-only'
            })
            .pipe(
                map(response => {
                    if (response?.data?.findNoticeTypes) {
                        return response.data.findNoticeTypes;
                    }
                    throw new Error(
                        'Find LOTAM types by organisation query failed'
                    );
                })
            );
    }

    createNoticeType(command: CreateNoticeTypeCommand) {
        return this.apollo
            .mutate<{ createNoticeType: NoticeType }>({
                mutation: gql`
                    mutation createNoticeType(
                        $command: CreateNoticeTypeCommand!
                    ) {
                        createNoticeType(command: $command) {
                            id
                            name
                            archived
                            description
                            icon
                            iconFamily
                            colour
                            audience
                        }
                    }
                `,
                variables: {
                    command
                }
            })
            .pipe(
                map(response => {
                    if (response?.data?.createNoticeType != null) {
                        return <NoticeType>response.data.createNoticeType;
                    }
                    throw new Error('Creating notice type failed');
                }),
                tap(result => {
                    this.changeSource.next({
                        type: ChangeDetails.Type.CREATE,
                        changedId: result.id
                    });
                })
            );
    }

    updateNoticeType(command: UpdateNoticeTypeCommand) {
        return this.apollo
            .mutate<{ updateNoticeType: NoticeType }>({
                mutation: gql`
                    mutation updateNoticeType(
                        $command: UpdateNoticeTypeCommand!
                    ) {
                        updateNoticeType(command: $command) {
                            id
                            name
                            archived
                            description
                            icon
                            iconFamily
                            colour
                            audience
                        }
                    }
                `,
                variables: {
                    command
                }
            })
            .pipe(
                map(response => {
                    if (response?.data?.updateNoticeType != null) {
                        return <NoticeType>response.data.updateNoticeType;
                    }
                    throw new Error('Updating notice type failed');
                }),
                tap(result => {
                    this.changeSource.next({
                        type: ChangeDetails.Type.UPDATE,
                        changedId: result.id
                    });
                })
            );
    }

    archiveNoticeType(noticeTypeId: number) {
        return this.apollo
            .mutate<{ archiveNoticeType: NoticeType }>({
                mutation: gql`
                    mutation archiveNoticeType($noticeTypeId: Long!) {
                        archiveNoticeType(noticeTypeId: $noticeTypeId) {
                            id
                            name
                            archived
                            description
                            icon
                            iconFamily
                            colour
                            audience
                        }
                    }
                `,
                variables: {
                    noticeTypeId
                }
            })
            .pipe(
                map(response => {
                    if (response?.data?.archiveNoticeType != null) {
                        return <NoticeType>response.data.archiveNoticeType;
                    }
                    throw new Error('Archiving notice type failed');
                }),
                tap(result => {
                    this.changeSource.next({
                        type: ChangeDetails.Type.UPDATE,
                        changedId: result.id
                    });
                })
            );
    }

    unarchiveNoticeType(noticeTypeId: number) {
        return this.apollo
            .mutate<{ unarchiveNoticeType: NoticeType }>({
                mutation: gql`
                    mutation unarchiveNoticeType($noticeTypeId: Long!) {
                        unarchiveNoticeType(noticeTypeId: $noticeTypeId) {
                            id
                            name
                            archived
                            description
                            icon
                            iconFamily
                            colour
                            audience
                        }
                    }
                `,
                variables: {
                    noticeTypeId
                }
            })
            .pipe(
                map(response => {
                    if (response?.data?.unarchiveNoticeType != null) {
                        return <NoticeType>response.data.unarchiveNoticeType;
                    }
                    throw new Error('Unarchiving notice type failed');
                }),
                tap(result => {
                    this.changeSource.next({
                        type: ChangeDetails.Type.UPDATE,
                        changedId: result.id
                    });
                })
            );
    }

    acknowledgeNotice(noticeId: number): Observable<boolean> {
        return this.apollo
            .mutate<{ acknowledgeNotice: ResultDto }>({
                mutation: gql`
                    mutation acknowledgeNotice($noticeId: Long!) {
                        acknowledgeNotice(noticeId: $noticeId) {
                            success
                        }
                    }
                `,
                variables: {
                    noticeId
                }
            })
            .pipe(
                map(response => {
                    if (response?.data?.acknowledgeNotice?.success) {
                        return response.data.acknowledgeNotice.success;
                    }
                    throw new Error('Acknowledge notice mutation failed');
                }),
                tap(result => {
                    this.changeSource.next({
                        type: ChangeDetails.Type.UPDATE,
                        changedId: noticeId
                    });
                })
            );
    }

    voidNoticeAcknowledgements(noticeId: number) {
        return this.apollo
            .mutate<{ voidNoticeAcknowledgements: ResultDto }>({
                mutation: gql`
                    mutation voidNoticeAcknowledgements($noticeId: Long!) {
                        voidNoticeAcknowledgements(noticeId: $noticeId) {
                            success
                        }
                    }
                `,
                variables: {
                    noticeId
                }
            })
            .pipe(
                map(response => {
                    if (response?.data?.voidNoticeAcknowledgements?.success) {
                        return response.data.voidNoticeAcknowledgements.success;
                    }
                }),
                tap(result => {
                    this.changeSource.next({
                        type: ChangeDetails.Type.UPDATE,
                        changedId: noticeId
                    });
                })
            );
    }

    acknowledgeMissionNotice(missionId: number, noticeId: number) {
        return this.http
            .put<MissionNoticeAcknowledgementDto>(
                `${this.baseUrl}/webapi/missions/${missionId}/notices/${noticeId}`,
                null
            )
            .pipe(
                tap(result => {
                    this.changeSource.next({
                        type: ChangeDetails.Type.UPDATE,
                        changedId: noticeId
                    });
                })
            );
    }
}
